// src/hooks/useEventDetail.ts
// EventDetail 組件的核心狀態管理 Hook - 實現 Local-first 樂觀更新
// 與 useTimeBoxDetail 行為一致，僅支援 edit 模式（建立由 Quick Create 處理）

import { useState, useEffect, useCallback, useRef } from 'react';
import { Event, EventCategory, EventTimeType } from '../../../types/calendar-event';
import { eventService } from '../../../services/api/calendar-events';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logError } from '../../../utils/logger';
import {
  getEventByIdLocal,
  createEventLocal,
  updateEventLocal,
  deleteEventLocal,
} from '../../../services/indexedDB';
import {
  calculateDuration,
  calculateNewEndTime
} from '../../../utils/timeBoxHelpers';
import { DRAG_CONFIG } from '../../../utils/calendarHelper';

// ============================================
// 類型定義
// ============================================

// 常數
const MIN_DURATION = DRAG_CONFIG.MIN_DURATION_MINUTES * 60 * 1000; // 毫秒

export interface UseEventDetailOptions {
  eventId: string | null;
  isOpen: boolean;
  onClose?: () => void;
  onRefresh?: () => void;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
}

export interface UseEventDetailReturn {
  // 狀態
  event: Event | null;
  isLoading: boolean;
  error: string | null;
  isSubmitting: boolean;

  // 更新函數
  updateTitle: (title: string) => Promise<void>;
  updateNotes: (notes: string | undefined) => Promise<void>;
  updateCategory: (category: EventCategory) => Promise<void>;
  updateStartTime: (startTime: Date) => Promise<void>;
  updateEndTime: (endTime: Date) => Promise<void>;
  updateTimeType: (timeType: EventTimeType) => Promise<void>;
  updateLocation: (location: string | undefined) => Promise<void>;

  // 操作
  deleteEvent: () => Promise<void>;
}

// ============================================
// Hook 實現
// ============================================

export function useEventDetail(options: UseEventDetailOptions): UseEventDetailReturn {
  const {
    eventId,
    isOpen,
    onClose,
    onRefresh,
    showSuccess,
    showError,
  } = options;

  // 核心狀態
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs 用於樂觀更新回滾
  const originalEventRef = useRef<Event | null>(null);

  // 記錄原始 duration（用於時間智能調整）
  const originalDurationRef = useRef<number>(0);

  // ============================================
  // 數據加載
  // ============================================

  const fetchEvent = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. 從 IndexedDB 快取讀取（快速顯示）
      const cachedEvent = await getEventByIdLocal(id);
      if (cachedEvent) {
        setEvent(cachedEvent);
        originalEventRef.current = cachedEvent;
        originalDurationRef.current = calculateDuration(
          cachedEvent.startTime,
          cachedEvent.endTime
        );
        setIsLoading(false);
        return;
      }

      // 2. IndexedDB 沒有，從 Firestore 讀取
      const firestoreEvent = await eventService.getEventById(id);
      if (firestoreEvent) {
        setEvent(firestoreEvent);
        originalEventRef.current = firestoreEvent;
        originalDurationRef.current = calculateDuration(
          firestoreEvent.startTime,
          firestoreEvent.endTime
        );

        // 3. 立即寫入 IndexedDB（確保後續更新不會失敗）
        try {
          await createEventLocal(firestoreEvent);
        } catch (cacheError) {
          logError('寫入 IndexedDB 失敗', cacheError);
          // 快取失敗不阻止顯示
        }
      } else {
        setError('Event not found');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      logError('Event 獲取失敗', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 監聽 eventId 變化
  useEffect(() => {
    if (isOpen && eventId) {
      fetchEvent(eventId);
    } else {
      setEvent(null);
      setError(null);
      originalEventRef.current = null;
      originalDurationRef.current = 0;
    }
  }, [isOpen, eventId, fetchEvent]);

  // ============================================
  // 樂觀更新輔助函數
  // ============================================

  const optimisticUpdate = useCallback(async <T extends Partial<Event>>(
    updates: T,
    apiCall: () => Promise<void>,
    successMessage?: { title: string; message: string }
  ): Promise<void> => {
    if (!event) return;

    // 保存原始狀態用於回滾
    const originalEvent = { ...event };
    originalEventRef.current = originalEvent;

    // 1. 樂觀更新本地狀態
    const updatedEvent = { ...event, ...updates };
    setEvent(updatedEvent);

    // 2. 更新 IndexedDB
    try {
      await updateEventLocal(event.id, updates);
    } catch (cacheError) {
      logError('IndexedDB 更新失敗', cacheError);
      // 快取失敗不阻止流程
    }

    try {
      // 3. 同步到 Firestore
      await apiCall();

      // 4. 發布事件通知其他組件
      eventBus.publish(EVENTS.EVENT.UPDATED, { ...updatedEvent, id: event.id });

      // 5. 顯示成功消息
      if (successMessage) {
        showSuccess(successMessage.title, successMessage.message);
      }

      // 6. 刷新父組件
      if (onRefresh) {
        onRefresh();
      }

    } catch (err) {
      // 回滾本地狀態
      setEvent(originalEvent);

      // 回滾 IndexedDB
      try {
        await updateEventLocal(event.id, originalEvent);
      } catch (revertError) {
        logError('IndexedDB 回滾失敗', revertError);
      }

      logError('Event 更新失敗', err);
      showError('Update Failed', 'Failed to update event. Please try again.');
      throw err;
    }
  }, [event, showSuccess, showError, onRefresh]);

  // ============================================
  // 更新函數
  // ============================================

  const updateTitle = useCallback(async (title: string) => {
    if (!event) return;

    await optimisticUpdate(
      { title },
      async () => {
        await eventService.updateEvent(event.id, { id: event.id, title });
      }
    );
  }, [event, optimisticUpdate]);

  const updateNotes = useCallback(async (notes: string | undefined) => {
    if (!event) return;

    await optimisticUpdate(
      { notes },
      async () => {
        await eventService.updateEvent(event.id, { id: event.id, notes });
      }
    );
  }, [event, optimisticUpdate]);

  const updateCategory = useCallback(async (category: EventCategory) => {
    if (!event) return;

    await optimisticUpdate(
      { category },
      async () => {
        await eventService.updateEvent(event.id, { id: event.id, category });
      }
    );
  }, [event, optimisticUpdate]);

  const updateStartTime = useCallback(async (startTime: Date) => {
    if (!event) return;

    // 基於原有 duration 計算新的結束時間
    const newEndTime = calculateNewEndTime(startTime, originalDurationRef.current);

    await optimisticUpdate(
      { startTime, endTime: newEndTime },
      async () => {
        await eventService.updateEvent(event.id, {
          id: event.id,
          startTime,
          endTime: newEndTime,
        });
      }
    );
  }, [event, optimisticUpdate]);

  const updateEndTime = useCallback(async (endTime: Date) => {
    if (!event) return;

    // 驗證：End Time 必須晚於 Start Time
    if (endTime <= event.startTime) {
      showError('Validation Error', 'End time must be later than start time');
      return;
    }

    // 驗證：Duration 至少 15 分鐘
    const duration = endTime.getTime() - event.startTime.getTime();
    if (duration < MIN_DURATION) {
      showError('Validation Error', 'Event must be at least 15 minutes');
      return;
    }

    // 更新 duration ref
    originalDurationRef.current = duration;

    await optimisticUpdate(
      { endTime },
      async () => {
        await eventService.updateEvent(event.id, { id: event.id, endTime });
      }
    );
  }, [event, optimisticUpdate, showError]);

  const updateTimeType = useCallback(async (timeType: EventTimeType) => {
    if (!event) return;

    if (timeType === 'allday') {
      // 切換到 All Day：保留日期，設定整天時間
      const startOfDay = new Date(event.startTime);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(event.endTime);
      endOfDay.setHours(23, 59, 59, 999);

      await optimisticUpdate(
        { timeType, startTime: startOfDay, endTime: endOfDay },
        async () => {
          await eventService.updateEvent(event.id, {
            id: event.id,
            timeType,
            startTime: startOfDay,
            endTime: endOfDay,
          });
        }
      );
    } else {
      // 切換到 Timed：設定預設時間（9:00 - 10:00）
      const startTime = new Date(event.startTime);
      startTime.setHours(9, 0, 0, 0);

      const endTime = new Date(startTime);
      endTime.setHours(10, 0, 0, 0);

      // 更新 duration ref
      originalDurationRef.current = calculateDuration(startTime, endTime);

      await optimisticUpdate(
        { timeType, startTime, endTime },
        async () => {
          await eventService.updateEvent(event.id, {
            id: event.id,
            timeType,
            startTime,
            endTime,
          });
        }
      );
    }
  }, [event, optimisticUpdate]);

  const updateLocation = useCallback(async (location: string | undefined) => {
    if (!event) return;

    await optimisticUpdate(
      { location },
      async () => {
        await eventService.updateEvent(event.id, { id: event.id, location });
      }
    );
  }, [event, optimisticUpdate]);

  // ============================================
  // 刪除操作
  // ============================================

  const handleDeleteEvent = useCallback(async () => {
    if (!event) return;

    setIsSubmitting(true);

    try {
      // 1. 立即從 IndexedDB 刪除
      await deleteEventLocal(event.id);

      // 2. 同步到 Firestore
      await eventService.deleteEvent(event.id);

      // 3. 發布事件
      eventBus.publish(EVENTS.EVENT.DELETED, { id: event.id });

      // 4. 顯示成功消息
      showSuccess('Deleted', 'Event deleted successfully');

      // 5. 刷新父組件
      if (onRefresh) {
        onRefresh();
      }

      // 6. 關閉 modal
      if (onClose) {
        onClose();
      }

    } catch (err) {
      logError('Event 刪除失敗', err);
      showError('Delete Failed', 'Failed to delete event. Please try again.');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [event, showSuccess, showError, onRefresh, onClose]);

  // ============================================
  // 返回值
  // ============================================

  return {
    // 狀態
    event,
    isLoading,
    error,
    isSubmitting,

    // 更新函數
    updateTitle,
    updateNotes,
    updateCategory,
    updateStartTime,
    updateEndTime,
    updateTimeType,
    updateLocation,

    // 操作
    deleteEvent: handleDeleteEvent,
  };
}
